import { Building2, ChevronRight, Pencil, Ruler, Trash2 } from "lucide-react";
import { useMemo } from "react";
import Flag from "react-world-flags";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaPopover } from "@/shared/components/overlays/ColocaPopover";
import useTranslations from "@/shared/lang/useTranslations";
import type { LimitPayerType } from "@/shared/services/payments/paymentCorridors.service";
import { formatRail } from "@/shared/utils/railDisplay";

import { RAIL_COUNTRY } from "../../constants/paymentRails.constants";
import type { AmountLimitCell } from "../../types/paymentLimits.types";
import { formatSince } from "../rails/railCellDisplay";
import { effectiveFor, formatBound, hasOwnOverride, rowsInScope, sourceKeyFor } from "./limitCellDisplay";

export interface AmountLimitsTableProps {
  /** Cells of ONE direction — the page renders a table per section (payins / payouts). */
  cells: AmountLimitCell[];
  isLoading: boolean;
  skeletonRows: number;
  /** Global view (null) or the organization whose effective bounds the rows show. */
  scopeOrgId: string | null;
  orgNameById: Map<string, string>;
  isMutating: boolean;
  onEdit: (cell: AmountLimitCell) => void;
  /** Removes every live row of the cell in the current scope (global rows, or the organization's override). */
  onRemove: (cell: AmountLimitCell) => void;
  /** Jumps the page scope to an organization — the overrides list is a shortcut into its view. */
  onViewOrg: (organizationId: string) => void;
}

/** The two bounds of one payer kind, stacked; "no limit" when neither side is set. */
function BoundsCell({
  cell,
  payerType,
  locale,
  labels,
}: {
  cell: AmountLimitCell;
  payerType: LimitPayerType;
  locale: string;
  labels: { min: string; max: string; none: string };
}) {
  const limit = effectiveFor(cell, payerType);
  if (!limit || (limit.min_amount === null && limit.max_amount === null)) {
    return <span className="text-xs text-muted-foreground">{labels.none}</span>;
  }
  return (
    <div className="font-mono text-xs tabular-nums">
      <div>
        <span className="text-muted-foreground">{labels.min} </span>
        {formatBound(limit.min_amount, cell.currency, locale, "—")}
      </div>
      <div>
        <span className="text-muted-foreground">{labels.max} </span>
        {formatBound(limit.max_amount, cell.currency, locale, "—")}
      </div>
    </div>
  );
}

/**
 * One row per corridor of a single direction. The two bound columns show the EFFECTIVE range per payer kind
 * for the current scope; the source column says which level produced it (none / global / the organization's
 * own override), because with most-specific-wins the same figure can come from two places — and removing it
 * differs in each.
 */
export function AmountLimitsTable({
  cells,
  isLoading,
  skeletonRows,
  scopeOrgId,
  orgNameById,
  isMutating,
  onEdit,
  onRemove,
  onViewOrg,
}: AmountLimitsTableProps) {
  const { t, activeLanguage } = useTranslations("paymentLimits");

  const columns = useMemo<ColocaDataTableColumn<AmountLimitCell>[]>(() => {
    const boundLabels = { min: t("bound_min"), max: t("bound_max"), none: t("bound_none") };
    const base: ColocaDataTableColumn<AmountLimitCell>[] = [
      {
        id: "rail",
        header: t("th_rail"),
        cell: (cell) => (
          <ColocaBadge variant="secondary" className="font-sans">
            {formatRail(cell.rail)}
          </ColocaBadge>
        ),
      },
      {
        id: "country",
        header: t("th_country"),
        cell: (cell) => {
          const country = RAIL_COUNTRY[cell.rail];
          if (!country) return <span className="text-muted-foreground">{cell.country}</span>;
          return (
            <span className="flex items-center gap-2">
              <Flag code={country.iso2} className="h-4 w-6 shrink-0 rounded object-cover" />
              <span>{t(country.labelKey)}</span>
            </span>
          );
        },
      },
      {
        id: "currency",
        header: t("th_currency"),
        cell: (cell) => <span className="font-mono text-xs">{cell.currency}</span>,
      },
      {
        id: "person",
        header: t("th_person"),
        cell: (cell) => (
          <BoundsCell cell={cell} payerType="PERSON" locale={activeLanguage} labels={boundLabels} />
        ),
      },
      {
        id: "company",
        header: t("th_company"),
        cell: (cell) => (
          <BoundsCell cell={cell} payerType="COMPANY" locale={activeLanguage} labels={boundLabels} />
        ),
      },
      {
        id: "source",
        header: t("th_source"),
        cell: (cell) => {
          const rows = rowsInScope(cell, scopeOrgId);
          const latest = rows
            .map((row) => row.since)
            .sort()
            .at(-1);
          const reason = rows.find((row) => row.reason)?.reason;
          return (
            <div className="text-xs text-muted-foreground">
              <div>
                {t(sourceKeyFor(cell, scopeOrgId))}
                {latest ? ` · ${formatSince(latest, activeLanguage)}` : ""}
              </div>
              {reason && <div className="max-w-56 truncate">{reason}</div>}
            </div>
          );
        },
      },
    ];

    if (!scopeOrgId) {
      base.push({
        id: "overrides",
        header: t("th_overrides"),
        cell: (cell) => {
          if (cell.org_overrides.length === 0) {
            return <span className="text-muted-foreground">—</span>;
          }
          return (
            <ColocaPopover
              align="start"
              contentClassName="w-80"
              description={t("overrides_popover_hint")}
              trigger={
                <ColocaButton variant="secondary" size="xs" icon={Building2}>
                  {t("overrides_count", { count: cell.org_overrides.length })}
                </ColocaButton>
              }
            >
              <div className="flex flex-col gap-1">
                {cell.org_overrides.map((override) => (
                  <ColocaButton
                    key={override.organization_id}
                    variant="ghost"
                    size="sm"
                    className="w-full justify-between gap-2"
                    onClick={() => onViewOrg(override.organization_id)}
                  >
                    <span className="truncate">
                      {orgNameById.get(override.organization_id) ?? override.organization_id}
                    </span>
                    <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
                  </ColocaButton>
                ))}
              </div>
            </ColocaPopover>
          );
        },
      });
    }

    base.push({
      id: "action",
      header: "",
      headerClassName: "text-right",
      cellClassName: "text-right",
      cell: (cell) => {
        const removable = scopeOrgId ? hasOwnOverride(cell, scopeOrgId) : cell.global.length > 0;
        return (
          <span className="inline-flex items-center justify-end gap-1">
            {removable && (
              <ColocaButton
                variant="ghost"
                size="xs"
                icon={Trash2}
                disabled={isMutating}
                onClick={() => onRemove(cell)}
              >
                {t(scopeOrgId ? "action_clear_override" : "action_remove")}
              </ColocaButton>
            )}
            <ColocaButton
              variant="secondary"
              size="xs"
              icon={Pencil}
              disabled={isMutating}
              onClick={() => onEdit(cell)}
            >
              {t("action_edit")}
            </ColocaButton>
          </span>
        );
      },
    });

    return base;
  }, [t, activeLanguage, scopeOrgId, orgNameById, isMutating, onEdit, onRemove, onViewOrg]);

  return (
    <ColocaDataTable
      columns={columns}
      rows={cells}
      rowKey={(cell) => `${cell.rail}:${cell.direction}:${cell.currency}`}
      loading={isLoading}
      skeletonRows={skeletonRows}
      // Eight columns in the global view — below this the table scrolls instead of crushing them.
      minWidth={1040}
      empty={
        <ColocaEmpty
          icon={Ruler}
          title={t("empty_title")}
          description={t("empty_description")}
          className="border-0"
        />
      }
    />
  );
}
